/**
 * Búsqueda de direcciones: Esri primero (mejor cobertura en Chile), Nominatim
 * como respaldo.
 *
 * Dos decisiones que vienen de haberlas pagado antes:
 *
 * 1. **`fetch`, no JSONP.** El HTML original usaba JSONP porque se abría con
 *    `file://`, donde Chrome bloquea `fetch`. Servida por HTTP la app ya no
 *    necesita ese truco, y JSONP inyecta `<script>` de terceros en la página.
 *
 * 2. **Caché de sugerencias.** Nominatim limita a 1 request por segundo. El
 *    autocompletado es la ruta más caliente de la app, así que se cachea; sin
 *    eso el servicio empieza a responder "sin conexión" estando sano. Los
 *    fallos NO se cachean a propósito: una caída hay que reintentarla, no
 *    recordarla.
 */

const ESRI_GEOCODE =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates';
const NOMINATIM_SEARCH = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_REVERSE = 'https://nominatim.openstreetmap.org/reverse';

/** Resultados por consulta normalizada. Sólo se guardan las respuestas exitosas. */
const suggestionCache = new Map();
const CACHE_MAX_ENTRIES = 200;

function cacheGet(key) {
  return suggestionCache.get(key);
}

function cacheSet(key, value) {
  if (suggestionCache.size >= CACHE_MAX_ENTRIES) {
    suggestionCache.delete(suggestionCache.keys().next().value);
  }
  suggestionCache.set(key, value);
}

async function fetchJson(url, signal) {
  const response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

async function searchEsri(query, center, signal) {
  const params = new URLSearchParams({
    f: 'json',
    maxLocations: '6',
    outFields: 'Match_addr',
    countryCode: 'CL',
    singleLine: query,
    location: `${center.lng},${center.lat}`,
  });
  const data = await fetchJson(`${ESRI_GEOCODE}?${params}`, signal);
  if (!data?.candidates?.length) return [];
  return data.candidates.map((candidate) => ({
    lat: candidate.location.y,
    lon: candidate.location.x,
    title: candidate.address || 'Lugar',
    subtitle: '',
  }));
}

async function searchNominatim(query, signal) {
  const params = new URLSearchParams({
    format: 'json',
    addressdetails: '1',
    limit: '6',
    q: query,
  });
  const data = await fetchJson(`${NOMINATIM_SEARCH}?${params}`, signal);
  if (!data?.length) return [];
  return data.map((item) => {
    const parts = (item.display_name || '').split(',');
    return {
      lat: Number(item.lat),
      lon: Number(item.lon),
      title: parts.slice(0, 2).join(',').trim(),
      subtitle: parts.slice(2, 4).join(',').trim(),
    };
  });
}

/**
 * Busca un lugar.
 *
 * @returns {Promise<Array|null>} Lista de resultados (vacía = sin coincidencias),
 *   o `null` cuando ningún proveedor pudo contestar. Distinguir ambos casos
 *   importa: "no encontré" y "no pude preguntar" son mensajes distintos para el
 *   operador.
 */
export async function geocode(query, center, signal) {
  const key = query.trim().toLowerCase();
  const cached = cacheGet(key);
  if (cached) return cached;

  let anyProviderAnswered = false;

  try {
    const results = await searchEsri(query, center, signal);
    anyProviderAnswered = true;
    if (results.length) {
      cacheSet(key, results);
      return results;
    }
  } catch (error) {
    if (error.name === 'AbortError') throw error;
  }

  try {
    const results = await searchNominatim(query, signal);
    anyProviderAnswered = true;
    if (results.length) {
      cacheSet(key, results);
      return results;
    }
  } catch (error) {
    if (error.name === 'AbortError') throw error;
  }

  return anyProviderAnswered ? [] : null;
}

/** Dirección legible a partir de una coordenada. `null` si no se pudo resolver. */
export async function reverseGeocode(lat, lon, signal) {
  try {
    const params = new URLSearchParams({
      format: 'json',
      'accept-language': 'es',
      lat: String(lat),
      lon: String(lon),
    });
    const data = await fetchJson(`${NOMINATIM_REVERSE}?${params}`, signal);
    const address = data?.address || {};
    const street = [address.road, address.house_number].filter(Boolean).join(' ');
    const town =
      address.city || address.town || address.village || address.suburb || address.county || '';
    const composed = [street, town].filter(Boolean).join(', ');
    if (composed) return composed;
    if (data?.display_name) return data.display_name.split(',').slice(0, 3).join(',').trim();
  } catch {
    /* sin dirección legible */
  }
  return null;
}

/** ¿El texto es un par de coordenadas pegado? Devuelve [lat, lng] o `null`. */
export function parseCoordinates(text) {
  const match = text
    .trim()
    .match(/^\s*(-?\d{1,3}\.\d+)\s*[,\s]\s*(-?\d{1,3}\.\d+)\s*$/);
  if (!match) return null;
  return [Number.parseFloat(match[1]), Number.parseFloat(match[2])];
}

/** Sólo para tests: vacía la caché de sugerencias. */
export function clearGeocodeCache() {
  suggestionCache.clear();
}
